import styled, { keyframes } from "styled-components";
import { colors } from "src/theme/colors";
import { line, radii, space } from "src/theme/ui";

/**
 * Exactly one copy's width, in pixels, handed in as a custom property.
 *
 * Not a percentage of the rail. Half the rail is one copy only while there
 * are exactly two of them, and the number of copies depends on how many
 * fit across the card — so the shift has to be the copy's own width, or the
 * loop lands mid-figure and the tape appears to jump.
 */
const run = keyframes`
  from {
    transform: translate3d(0, 0, 0);
  }
  to {
    transform: translate3d(calc(var(--tape-shift, 50%) * -1), 0, 0);
  }
`;

/**
 * Every copy of the readout, moving as one.
 *
 * Duration is set inline from the measured width of one copy divided by a
 * fixed speed, so the tape runs at the same pace on a phone as on a 1400px
 * card and does not change speed when a figure gains a digit. Sized by its
 * contents rather than by the card: a marquee that fills its container has
 * nothing to travel.
 */
export const Rail = styled.div`
  display: flex;
  width: max-content;
  animation-name: ${run};
  animation-timing-function: linear;
  animation-iteration-count: infinite;
  will-change: transform;
`;

/**
 * One copy of the figures. The rest are aria-hidden — a screen reader should
 * hear the readout once.
 *
 * Never narrower than the tape, and spread to fill it: a min-width is set
 * inline from the measured bar. Nine figures do not fill a 1096px card, and
 * a copy narrower than the window it runs through means the window shows the
 * end of one copy and the start of the next AT THE SAME TIME — so the same
 * period appeared twice on screen, once near each edge, and read as two
 * different readings that happened to agree. A copy at least as wide as the
 * bar can only ever show one boundary, so every figure on screen is a
 * different figure.
 */
export const Run = styled.div`
  display: flex;
  align-items: stretch;
  flex: 0 0 auto;
`;

/**
 * The tape.
 *
 * A running readout rather than a control. It had been a period SELECTOR
 * sitting directly above the chart's own range pills, which is the same
 * concept twice; the chart owns the period again, and this says what the
 * smartfolio has returned over every horizon at once.
 *
 * Three things a moving readout has to get right:
 *
 * IT PAUSES UNDER THE POINTER. Motion is pre-attentive — it is caught
 * before it is looked at, which is why a tape draws the eye and why a
 * reader cannot hold a moving four-digit figure long enough to compare it
 * with another. Hovering stops it, so reading and noticing are both
 * available instead of trading against each other.
 *
 * IT STOPS ENTIRELY FOR ANYONE WHO ASKS. Sustained scrolling motion is a
 * vestibular trigger, so under prefers-reduced-motion the animation is
 * removed and the same figures become a plain scrolling row.
 *
 * IT FADES AT BOTH ENDS. A figure cut off by a hard edge reads as a broken
 * number; under a mask it reads as one that has not arrived yet.
 */
export const Tape = styled.div`
  overflow: hidden;
  width: 100%;
  padding: ${space.sm}px 0;
  border-radius: ${radii.lg}px;
  border: 1px solid ${line.hairline};
  background: ${colors.white};
  -webkit-mask-image: linear-gradient(
    to right,
    transparent,
    #000 32px,
    #000 calc(100% - 32px),
    transparent
  );
  mask-image: linear-gradient(
    to right,
    transparent,
    #000 32px,
    #000 calc(100% - 32px),
    transparent
  );

  &:hover ${Rail}, &:focus-within ${Rail} {
    animation-play-state: paused;
  }

  @media (prefers-reduced-motion: reduce) {
    overflow-x: auto;

    ${Rail} {
      animation: none;
    }

    /* One copy is the readout; a second would be a repeat to read past. */
    ${Run} + ${Run} {
      display: none;
    }
  }
`;

/**
 * One figure on the tape, in a cell of the same width as every other.
 *
 * Equal cells rather than figures spread with space-between. Spreading
 * distributes the SURPLUS between items of unequal width, so the gap after
 * "12M 17.19%" came out narrower than the gap after "3Y 25.57%" and the
 * hairlines between them landed at irregular intervals — and the seam
 * between one copy and the next was a visibly different gap from all the
 * rest, which is what gave the first figure after it away. Growing every
 * cell by the same amount puts the dividers on an even pitch, so the loop
 * has no tell.
 *
 * It never shrinks below its content: a figure broken across two lines is
 * worse than a tape that has to scroll.
 */
export const Item = styled.div`
  display: flex;
  /* An equal SHARE, not an equal share of the surplus. Growing from each
     cell's own content width adds the same amount to unequal bases, so a
     figure with an extra digit still ended up in a wider cell and the
     dividers came out 15px off a regular pitch. A zero basis makes every
     cell the same width; max-content keeps one from ever being narrower
     than the figure in it. */
  flex: 1 0 0;
  min-width: max-content;
  align-items: center;
  justify-content: center;
  padding: 2px ${space.md}px;
  border-right: 1px solid ${line.hairline};
  white-space: nowrap;
`;

/**
 * The returns tape, while the returns are still being worked out.
 *
 * It was an Ant Design Tag in `color="orange"`: cream fill, orange border,
 * orange text, and a spinning glyph pressed flat against the first letter
 * because a Tag's icon slot has no gap. Orange in this product is a warning,
 * and a calculation in progress is not one — nothing is wrong, the figures
 * simply are not in yet.
 *
 * So it reads as what it is: a quiet strip in the shape the tape itself will
 * take, in neutral ink, with the spinner given room and a line saying what
 * the reader is waiting for.
 */
export const Pending = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 9px;
  padding: 9px 14px;
  border: 1px solid ${line.hairline};
  border-radius: ${radii.lg}px;
  background: ${colors.gray[25]};
  font-size: 12.5px;
  color: ${colors.gray[600]};

  > svg {
    flex: none;
    color: ${colors.gray[400]};
  }
`;
